import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { ModalLarge } from "./ModalComponets";

const ActionDetailModal = ({ open, onClose, userId }) => {
    const [domainList, setDomainList] = useState([]);
    const [selectedDomain, setSelectedDomain] = useState(null);
    const [loading, setLoading] = useState(false);

    const [revenueData, setRevenueData] = useState([]);
    const [revenueLoading, setRevenueLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    /* ================= FETCH DOMAINS ================= */
    useEffect(() => {
        if (!open || !userId) return;

        const fetchDomains = async () => {
            setLoading(true);
            try {
                const response = await fetch(
                    "https://api.myad360.com/api/v1/FNV89uJ9sud/admin/get_domain.php",
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Accept: "application/json",
                            token: "fYb8PeU8SXA6RWtbDbEpAclJ3qqHCy4EIdeyZQfVSPD8kdXOlrCphzADys2QmPaBvFGveDs",
                        },
                        body: JSON.stringify({
                            page: 0,
                            perpage: 50,
                            user_id: userId,
                        }),
                    }
                );

                const result = await response.json();
                setDomainList(result?.data || []);
            } catch (error) {
                console.error("Domain fetch error:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchDomains();
    }, [open, userId]);

    /* ================= FETCH ONLY INACTIVE REVENUE DATA ================= */
    useEffect(() => {
        if (!selectedDomain || !userId) return;

        const fetchRevenueData = async () => {
            setRevenueLoading(true);
            try {
                const response = await fetch(
                    "https://api.myad360.com/api/v1/FNV89uJ9sud/admin/get_revenue_data.php",
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Accept: "application/json",
                            token: "fYb8PeU8SXA6RWtbDbEpAclJ3qqHCy4EIdeyZQfVSPD8kdXOlrCphzADys2QmPaBvFGveDs",
                        },
                        body: JSON.stringify({
                            page: 0,
                            perpage: 10,
                            user_id: userId,
                            domain_id: selectedDomain.id,
                            start_date: "",
                            end_date: "",
                            is_active: "0", // ✅ ONLY INACTIVE
                        }),
                    }
                );

                const result = await response.json();
                setRevenueData(result?.data || []);
            } catch (error) {
                console.error("Revenue fetch error:", error);
            } finally {
                setRevenueLoading(false);
            }
        };

        fetchRevenueData();
    }, [selectedDomain, userId]);

    /* ================= ACTIVATE (INACTIVE → ACTIVE) ================= */
    const changeRevenueStatus = async (row) => {
        if (!selectedDomain) return;

        const payload = {
            user_id: userId,
            domain_id: selectedDomain.id,
            revenue_id: row.revenue_id,
            is_active: "0", // ✅ ACTIVATE
        };

        setSubmitting(true);
        try {
            const response = await fetch(
                "https://api.myad360.com/api/v1/FNV89uJ9sud/admin/revenue_change_status.php",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        token: "3KHvb3JSNWD8uFnQKj3BqjbtuTg2J0ON9lr4kmjOtzmajhwdGYVgx6omv89UEqliEhA36jy",
                    },
                    body: JSON.stringify(payload),
                }
            );

            const result = await response.json();

            if (result?.status == 1) {
                // 🔁 remove activated row from table
                setRevenueData(prev =>
                    prev.filter(r => r.revenue_id !== row.revenue_id)
                );
            } else {
                alert(result?.msg || "Status update failed");
            }
        } catch {
            alert("Network error");
        } finally {
            setSubmitting(false);
        }
    };

    /* ================= TABLE COLUMNS ================= */
    const columns = [
        { name: "Date", selector: row => row.created_date },
        { name: "Revenue ($)", selector: row => row.revenue },
        { name: "CPM", selector: row => row.cpm },
        { name: "Fill Rate", selector: row => row.fill_rate },
        { name: "Impressions", selector: row => row.impressions },
        { name: "Clicks", selector: row => row.click },
        { name: "CTR", selector: row => row.ctr },
        {
            name: "Status",
            cell: () => (
                <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-700">
                    Inactive
                </span>
            ),
        },
        {
            name: "Action",
            cell: (row) => (
                <button
                    onClick={() => changeRevenueStatus(row)}
                    disabled={submitting}
                    className="px-2 py-1"
                >
                    ✅
                </button>
            ),
        },
    ];

    return (
        <ModalLarge isOpen={open} onClose={onClose} hideFooter>
            <div className="p-6 max-h-[80vh] overflow-y-auto">
                <h2 className="text-xl font-semibold mb-4">
                    Inactive Revenue Records (User ID: {userId})
                </h2>

                {/* ===== DOMAIN SELECT ===== */}
                <div className="mb-5">
                    <label className="block text-sm font-medium mb-1">
                        Select User | Application
                    </label>

                    <select
                        size={5}
                        className="w-full border rounded px-3 py-2 max-h-48 overflow-y-auto"
                        value={selectedDomain?.id || ""}
                        onChange={(e) =>
                            setSelectedDomain(
                                domainList.find(
                                    (d) => String(d.id) === e.target.value
                                )
                            )
                        }
                    >
                        <option value="">
                            {loading ? "Loading..." : "-- Select --"}
                        </option>

                        {domainList.map((d) => (
                            <option key={d.id} value={d.id}>
                                {d.full_name} | {d.app_name}
                            </option>
                        ))}
                    </select>
                </div>

                {/* ===== TABLE ===== */}
                {selectedDomain && (
                    <div className="border rounded">
                        <DataTable
                            columns={columns}
                            data={revenueData}
                            progressPending={revenueLoading}
                            highlightOnHover
                            pagination
                            conditionalRowStyles={[
                                {
                                    when: () => true,
                                    style: {
                                        backgroundColor: "#fff5f5",
                                    },
                                },
                            ]}
                        />
                    </div>
                )}
            </div>
        </ModalLarge>
    );
};

export default ActionDetailModal;
